<template>
  <div>
    <label>{{ title }}</label>
    <div class="d-flex">
      <div :class="`d-flex time-component ${disabled ? 'disabled' : ''}`">
        <div>
          <RsDropDownScroll
            :selected="value.startTime"
            :pick-options="startTimeOptions"
            :is-plain-options="$i18n.locale === 'en'"
            class="ml-2 w-100"
            :disabled="disabled"
            @onselect="handleStartSelect"
          />
        </div>
        <label class="to">{{ $ts('toursSettings.common.to') }}</label>
        <div class="mr-3">
          <RsDropDownScroll
            :selected="value.endTime || endTimeList[0]"
            :pick-options="endTimeOptions"
            :is-plain-options="$i18n.locale === 'en'"
            class="w-100"
            :disabled="disabled"
            @onselect="handleEndSelect"
          />
        </div>
      </div>
      <div class="clock">
        <i class="far fa-clock" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ISelectOption } from '@/shared';

interface ITimeSlot {
  startTime: string,
  endTime: string,
}
export default defineComponent({
  name: 'RsTimeInterval',
  props: {
    /**
     * increment of time interval in minutes.
     */
    increment: {
      type: Number,
      default: 30,
    },
    /**
     * `v-model` bound value that determines the start and end time for the interval.
     */
    value: {
      type: Object as () => ITimeSlot,
      default: () => ({ startTime: '10:00 am', endTime: '5:00 pm' } as ITimeSlot),
    },
    /**
     * Title of the field
     */
    title: {
      type: String,
      default: 'Select Time Component',
    },
    /**
     * Start Time Cut Off - will remove all the time options before the specified value
     */
    startTimeCutOff: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      startTimeList: [] as string[],
      endTimeList: [] as string[],
      dropdown: false,
    };
  },
  computed: {
    startTimeOptions(): Array<string | ISelectOption> {
      if (this.$i18n.locale === 'en') return this.startTimeList;
      return this.startTimeList.map((t) => ({ value: t, text: this.toFrenchTime(t) }));
    },
    endTimeOptions(): Array<string | ISelectOption> {
      if (this.$i18n.locale === 'en') return this.endTimeList;
      return this.endTimeList.map((t) => ({ value: t, text: this.toFrenchTime(t) }));
    },
    /**
      * Generate an array of time starting at 12:00 am to 11:59pm by `this.increment`
      */
    getTimeArray() {
      return Array(((24 * 60) / this.increment) + 1)
        .fill(0)
        .map((_, index) => {
          const totalMinutes = index * this.increment;
          const hours = Math.floor(totalMinutes / 60);
          const minutes = (totalMinutes / 60 - hours) * 60;
          const minuteString = minutes <= 9 ? `0${minutes}` : minutes.toString();
          return `${hours % 12 === 0 ? 12 : hours % 12}:${minuteString} ${
            hours < 12 ? 'am' : 'pm'
          }`;
        });
    },
  },
  mounted() {
    this.initData();
    this.startTimeList = this.getTimeArray.slice(0, this.getTimeArray.length - 1);
    this.endTimeList = this.getTimeArray.slice(
      this.getTimeArray.indexOf(this.value.startTime) + 1,
    );
    if (this.startTimeCutOff) {
      this.startTimeList = this.startTimeList.splice(this.startTimeList.indexOf(this.startTimeCutOff));
    }
  },
  methods: {
    /**
     * Select Start Value. Adjust end time array to only pick start + 1 item.
     */
    handleStartSelect(item: string) {
      this.value.startTime = item;
      this.endTimeList = this.getTimeArray.slice(
        this.getTimeArray.indexOf(item) + 1,
      );
      const endValue = this.endTimeList[0];
      this.value.endTime = endValue;

      this.$emit('change', this.value);
    },
    handleEndSelect(item: string) {
      this.value.endTime = item;
      this.$emit('change', this.value);
    },
    showDropdown(): void {
      this.dropdown = !this.dropdown;
    },
    initData() {
      this.startTimeList = this.getTimeArray.slice(0, this.getTimeArray.length - 1);
      this.endTimeList = this.getTimeArray.slice(
        this.getTimeArray.indexOf(this.value.startTime) + 1,
      );
    },
    toFrenchTime(time: string): string {
      const match = time.match(/^(\d+):(\d+)\s*(am|pm)$/i);
      if (!match) return time;
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const period = match[3].toLowerCase();
      if (period === 'am') {
        hours = hours === 12 ? 0 : hours;
      } else {
        hours = hours === 12 ? 12 : hours + 12;
      }
      return `${hours}h${minutes}`;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
.wrapper {
  width: 150px;
}
.time-component {
  border: 1px solid $tertiary-gray;
  border-radius: 5px 0 0 5px;
  margin: 0;
  padding: 0;
  height: 41px;
}
.to {
  padding: 0.6rem 1.2rem 0.4rem 1rem;
}
.clock {
  height: 41px;
  width: 51px;
  padding-top: 0.5rem;
  text-align: center;
  margin-left: -0.03rem;
  border-radius: 0px 4px 4px 0;
  background-color: $secondary-color;
  color: white;
}
</style>
